import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import * as Sentry from '@sentry/react'

// Only the real deployed app sets VITE_ENVIRONMENT=production (via Azure SWA app
// settings, see infra/web/modules/staticWebApp/prod.bicepparam). Vite's own MODE is
// 'production' for ANY `vite build`/`vite preview` - including local builds, audits,
// and Lighthouse/e2e test runs - so gating on MODE alone sends real telemetry (using
// whatever VITE_SENTRY_DSN happens to be in .env.local) for every local production build.
const isDeployedProduction = import.meta.env.VITE_ENVIRONMENT === 'production'

/**
 * Builds the Sentry `beforeSend` filter. Extracted as a standalone factory (rather than
 * an inline closure in `Sentry.init`) so the filtering logic itself - the non-production
 * suppression and the ResizeObserver-loop noise filter - can be unit tested directly
 * without needing to exercise the full Sentry SDK initialization.
 */
export function createBeforeSend(isDeployedProduction: boolean) {
  return function beforeSend(
    event: Sentry.ErrorEvent,
    hint: Sentry.EventHint
  ): Sentry.ErrorEvent | null {
    // Defense in depth: `enabled` on Sentry.init already stops the SDK from capturing
    // anything outside the real deployed app, but keep this as a second gate.
    if (!isDeployedProduction) {
      console.log('Sentry event (not sent - not the deployed production app):', event)
      return null
    }

    // Filter out known non-critical errors
    const error = hint.originalException
    if (error instanceof Error) {
      // Filter out ResizeObserver loop errors (browser quirk, not actionable)
      if (error.message.includes('ResizeObserver loop')) {
        return null
      }
    }

    return event
  }
}

// Initialize Sentry for error tracking and performance monitoring
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE, // 'production' only when actually deployed; otherwise the raw Vite mode, so any stray event is easy to spot as non-production
  enabled: isDeployedProduction, // Only send from the real deployed app - never from local/preview/test builds

  // Performance Monitoring
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true, // Protect user privacy by masking text
      blockAllMedia: true, // Protect user privacy by blocking media
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
  // We recommend adjusting this value in production (e.g., 0.1 for 10%)
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,

  // Set sampleRate to capture 100% of errors
  sampleRate: 1.0,

  // Session Replay - the public privacy policy promises replay only happens "in
  // case of errors", so we only capture on error (never a background sample of
  // ordinary sessions).
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  // Release tracking for better debugging
  release: `attraflow-web@${__APP_VERSION__}`,

  // Filter sensitive data
  beforeSend: createBeforeSend(isDeployedProduction),

  // Add custom tags
  initialScope: {
    tags: {
      'app.version': __APP_VERSION__,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
)
